import React from 'react';
import { Link } from 'react-router-dom';

const PLANS = [
  {
    key: 'starter',
    name: 'Starter',
    price: 79,
    audience: 'For Shopify teams comfortable installing the widget themselves',
    summary: 'A practical product-discovery and support assistant, with a lightweight monthly review to guide the next improvement.',
    features: [
      'Self-serve widget installation after preview',
      'Answers grounded in agreed product and support content',
      'Monthly lightweight performance snapshot based on available usage and question data',
      'One prioritized improvement recommendation each month',
      'Standard support for setup questions',
    ],
    note: 'Best for a straightforward first deployment.',
    href: '/trial?plan=starter&channel=shopify',
    cta: 'Start Starter trial',
  },
  {
    key: 'growth',
    name: 'Growth',
    price: 299,
    audience: 'For Shopify teams that want hands-on launch support',
    summary: 'Guided Shopify launch plus hands-on monthly improvements informed by unanswered questions and content gaps.',
    features: [
      'Guided onboarding and content setup',
      'Done-for-you widget installation assistance and QA',
      'Review of unanswered questions and content gaps',
      'One hands-on monthly improvement pass for answer quality and content gaps',
      'Support-workflow and escalation handoff review',
    ],
    note: 'Best fit when you want a more hands-on launch.',
    href: '/trial?plan=growth&channel=shopify',
    cta: 'Start Growth trial',
    featured: true,
  },
  {
    key: 'pro',
    name: 'Pro',
    price: 999,
    audience: 'For larger Shopify stores or more involved requirements',
    summary: 'A higher-touch engagement for broader knowledge coverage, customized reporting, and ongoing optimization.',
    features: [
      'Broader catalog or knowledge-base ingestion, scoped to your content',
      'Integration and connector planning where supported',
      'Customized reporting and performance reviews, scoped to agreed data',
      'Priority support and optimization, with scope agreed upfront',
    ],
    note: 'Specific integrations and deliverables are confirmed before work begins.',
    href: 'mailto:hello@scalabledeploy.com?subject=ScalableDeploy%20Pro%20inquiry',
    cta: 'Discuss Pro requirements',
    salesAssisted: true,
  },
];

function PlanCard({ plan }) {
  const cardClass = plan.featured
    ? 'border-indigo-300 bg-indigo-50 shadow-md ring-1 ring-indigo-200'
    : 'border-gray-200 bg-white shadow-sm';
  const ctaClass = plan.featured
    ? 'bg-indigo-600 text-white hover:bg-indigo-700'
    : 'border border-gray-300 text-gray-800 hover:bg-gray-50';

  return (
    <article className={`relative flex h-full flex-col rounded-xl border p-5 ${cardClass}`}>
      {plan.featured && (
        <div className="absolute right-4 top-4 rounded-full bg-indigo-100 px-2.5 py-1 text-xs font-semibold text-indigo-800">
          Most hands-on
        </div>
      )}
      <div className="text-sm font-semibold text-gray-500">{plan.name}{plan.salesAssisted ? ' · sales-assisted' : ''}</div>
      <div className="mt-2 text-3xl font-bold tracking-tight">
        ${plan.price}<span className="text-sm font-normal text-gray-500">/month</span>
      </div>
      <p className="mt-3 text-sm font-medium text-gray-800">{plan.audience}</p>
      <p className="mt-2 min-h-[3rem] text-sm text-gray-600">{plan.summary}</p>
      <div className="mt-4 border-t border-gray-200 pt-4">
        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">Included</div>
        <ul className="space-y-2 text-sm text-gray-700">
          {plan.features.map((feature) => (
            <li key={feature} className="flex items-start gap-2">
              <span aria-hidden="true" className="mt-0.5 font-bold text-indigo-600">✓</span>
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      </div>
      <p className="mt-4 text-xs leading-relaxed text-gray-500">{plan.note}</p>
      <div className="mt-auto pt-5">
        {plan.salesAssisted ? (
          <a href={plan.href} className={`block rounded-lg px-4 py-2.5 text-center text-sm font-medium ${ctaClass}`}>{plan.cta}</a>
        ) : (
          <Link to={plan.href} className={`block rounded-lg px-4 py-2.5 text-center text-sm font-medium ${ctaClass}`}>{plan.cta}</Link>
        )}
      </div>
    </article>
  );
}

export default function LandingPageShopify() {
  return (
    <div className="mx-auto max-w-6xl px-4 py-10">
      {/* HERO */}
      <section className="mb-12 text-center">
        <div className="mb-2 text-sm font-semibold uppercase tracking-wide text-indigo-600">AI assistant for Shopify stores</div>
        <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-indigo-200 bg-indigo-50 px-4 py-2 text-sm text-indigo-800">
          <span className="font-semibold">7-day trial:</span>
          private preview first · usually ~48 hours · install choice · trial snapshot
        </div>
        <h1 className="mb-4 text-3xl font-bold leading-tight sm:text-5xl">Turn your Shopify store knowledge into AI search + support.</h1>
        <p className="mx-auto mb-6 max-w-3xl text-base text-gray-700 sm:text-lg">
          Help shoppers discover products and get answers grounded in your catalog, FAQs, policies, and educational content.
          We prepare a private preview first—usually in about 48 hours—so you can test real customer questions before installation.
        </p>
        <div className="flex flex-col justify-center gap-3 sm:flex-row">
          <Link to="/trial?plan=growth&channel=shopify" className="rounded-lg bg-indigo-600 px-6 py-3 font-medium text-white shadow-md hover:bg-indigo-700">Start 7-day Shopify trial</Link>
          <Link to="/demo/rag?mode=dtc_supplements" className="rounded-lg border border-gray-300 px-6 py-3 font-medium text-gray-800 hover:bg-gray-50">View live demo</Link>
          <Link to="/trial?plan=starter&channel=shopify" className="px-6 py-3 font-medium text-indigo-600 hover:underline">Try Starter instead</Link>
        </div>
        <p className="mx-auto mt-4 max-w-2xl text-xs text-gray-500">
          A card is required to activate the trial. Your preview is prepared before installation. After the 7-day trial,
          the selected plan starts billing monthly unless you cancel beforehand.
        </p>
      </section>

      {/* PRODUCT VALUE */}
      <section className="mb-12">
        <div className="mb-5 text-center">
          <h2 className="mb-2 text-2xl font-semibold">Not another generic chat widget.</h2>
          <p className="mx-auto max-w-2xl text-sm text-gray-600">ScalableDeploy is built around Shopify product knowledge and common customer needs.</p>
        </div>
        <div className="mb-4 grid gap-4 md:grid-cols-2">
          <div className="rounded-xl border bg-white p-5 shadow-sm">
            <h3 className="mb-2 font-semibold">Product-aware answers</h3>
            <p className="text-sm text-gray-700">Help shoppers understand product fit, features, ingredients, subscriptions, and other pre-purchase questions using agreed store content.</p>
          </div>
          <div className="rounded-xl border bg-white p-5 shadow-sm">
            <h3 className="mb-2 font-semibold">Grounded policy answers</h3>
            <p className="text-sm text-gray-700">Make shipping, returns, subscription, and other support information easier to find when those details are present in your content.</p>
          </div>
          <div className="rounded-xl border bg-white p-5 shadow-sm">
            <h3 className="mb-2 font-semibold">Order lookup where configured</h3>
            <p className="text-sm text-gray-700">Connect a supported read-only order-status workflow where available and configured; otherwise, answer relevant shipping and returns questions from store content.</p>
          </div>
          <div className="rounded-xl border bg-white p-5 shadow-sm">
            <h3 className="mb-2 font-semibold">Support handoff planning</h3>
            <p className="text-sm text-gray-700">Define when a person should take over and what context should be passed along. Email or helpdesk delivery depends on the integration confirmed for your store.</p>
          </div>
        </div>
      </section>

      {/* WHY MERCHANTS USE IT */}
      <section className="mb-12 rounded-xl border-l-4 border-indigo-600 bg-indigo-50 p-6">
        <h2 className="mb-3 text-2xl font-semibold">Why Shopify merchants consider it</h2>
        <ul className="list-disc space-y-2 pl-5 text-sm text-gray-700">
          <li>Help shoppers answer product questions while they are deciding what to buy.</li>
          <li>Use responses grounded in your product pages, policies, FAQs, and support content.</li>
          <li>Make common information easier to find without routing every question to a human.</li>
          <li>Test a private preview before adding the widget to your live storefront.</li>
          <li>Review observed questions and content gaps before deciding whether to continue.</li>
        </ul>
      </section>

      {/* HOW IT WORKS */}
      <section className="mb-12 rounded-xl border bg-white p-6 shadow-sm">
        <h2 className="mb-4 text-2xl font-semibold">How the Shopify trial works</h2>
        <ol className="list-decimal space-y-2 pl-5 text-sm text-gray-700">
          <li><strong>Activate the trial:</strong> enter your work email and Shopify store URL, then confirm your card.</li>
          <li><strong>We prepare your workspace:</strong> curate agreed content and configure the assistant.</li>
          <li><strong>Test a private preview:</strong> it is usually ready in about 48 hours after setup and QA.</li>
          <li><strong>Choose your install path:</strong> use the self-serve instructions or request done-for-you installation assistance.</li>
          <li><strong>Review the trial snapshot:</strong> use observed questions, answer-quality notes, and content gaps to decide what to do next.</li>
        </ol>
      </section>

      {/* TRIAL SNAPSHOT */}
      <section className="mb-12 rounded-xl border bg-white p-6 shadow-sm">
        <div className="max-w-3xl">
          <div className="mb-2 text-sm font-semibold uppercase tracking-wide text-indigo-600">Included with the trial</div>
          <h2 className="mb-3 text-2xl font-semibold">A useful snapshot before you continue</h2>
          <p className="mb-4 text-sm text-gray-700">Near the end of the trial, we prepare a short review of what was observed and whether the assistant appears worth continuing, tuning, or stopping. Available metrics depend on what is instrumented and recorded during the trial.</p>
          <div className="grid gap-4 text-sm text-gray-700 md:grid-cols-2">
            <div className="rounded-lg border bg-gray-50 p-4">
              <h3 className="mb-2 font-semibold">What it may cover</h3>
              <ul className="list-disc space-y-1 pl-5">
                <li>Questions asked and common intent patterns</li>
                <li>Product, policy, or support topics that came up</li>
                <li>Fallbacks and content gaps identified during review</li>
                <li>Any available usage signals from the trial</li>
              </ul>
            </div>
            <div className="rounded-lg border bg-gray-50 p-4">
              <h3 className="mb-2 font-semibold">How it helps</h3>
              <ul className="list-disc space-y-1 pl-5">
                <li>Answer-quality observations</li>
                <li>Potential support and product-discovery improvements</li>
                <li>Recommended tuning priorities</li>
                <li>A clear next step: stop, tune, install, or continue</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* INSTALL PATHS */}
      <section className="mb-12">
        <h2 className="mb-5 text-2xl font-semibold">Choose your install path after preview</h2>
        <div className="grid gap-4 md:grid-cols-2">
          <div className="rounded-xl border bg-white p-6 shadow-sm">
            <div className="mb-1 text-sm text-gray-500">Done-for-you install assistance</div>
            <h3 className="mb-2 text-xl font-semibold">Get help getting it live</h3>
            <ul className="mb-5 list-disc space-y-1 pl-5 text-sm text-gray-700">
              <li>Guidance for a hands-off Shopify launch</li>
              <li>Widget installation assistance and QA</li>
              <li>Review of how the assistant fits your support process</li>
              <li>Best aligned with Growth</li>
            </ul>
            <Link to="/trial?plan=growth&channel=shopify" className="block rounded-lg bg-indigo-600 px-4 py-2 text-center text-white hover:bg-indigo-700">Start Growth trial</Link>
          </div>
          <div className="rounded-xl border bg-white p-6 shadow-sm">
            <div className="mb-1 text-sm text-gray-500">Self-serve install</div>
            <h3 className="mb-2 text-xl font-semibold">Use the install instructions</h3>
            <ul className="mb-5 list-disc space-y-1 pl-5 text-sm text-gray-700">
              <li>Best if you are comfortable adding a widget script</li>
              <li>Lower-touch path for initial validation</li>
              <li>Preview is prepared before installation</li>
              <li>Best aligned with Starter</li>
            </ul>
            <Link to="/trial?plan=starter&channel=shopify" className="block rounded-lg border px-4 py-2 text-center hover:bg-gray-50">Start Starter trial</Link>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="mb-12" id="pricing">
        <div className="mb-6 text-center">
          <h2 className="mb-2 text-2xl font-semibold">Choose the level of Shopify support you need</h2>
          <p className="mx-auto max-w-3xl text-sm text-gray-600">All plans center on an assistant grounded in agreed store content. Starter includes a lightweight monthly performance snapshot and one prioritized recommendation; Growth adds hands-on implementation of an improvement; Pro adds customized reporting and broader optimization. Snapshot detail depends on available recorded data. Monthly billing begins after the 7-day trial unless you cancel before it ends.</p>
        </div>
        <div className="grid items-stretch gap-4 md:grid-cols-3">
          {PLANS.map((plan) => <PlanCard key={plan.key} plan={plan} />)}
        </div>
        <p className="mt-4 text-xs leading-relaxed text-gray-500">Usage allowances, third-party costs, integration availability, and exact Pro scope should be confirmed before purchase where applicable. Order lookup and human handoff require a supported, configured workflow; they are not automatically included as live integrations on every store.</p>
      </section>

      {/* FAQ */}
      <section className="mb-12 rounded-xl border bg-white p-6 shadow-sm">
        <h2 className="mb-4 text-2xl font-semibold">Shopify trial questions</h2>
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <h3 className="mb-1 font-semibold">Do I need to install an app first?</h3>
            <p className="text-sm text-gray-600">The preview is prepared before installation. The installation path and any required store access are confirmed during setup.</p>
          </div>
          <div>
            <h3 className="mb-1 font-semibold">When does billing start?</h3>
            <p className="text-sm text-gray-600">A card is required to activate the 7-day trial. Your selected monthly subscription starts when the trial ends unless you cancel beforehand.</p>
          </div>
          <div>
            <h3 className="mb-1 font-semibold">Does order lookup work on every store?</h3>
            <p className="text-sm text-gray-600">Order lookup depends on a supported integration and configuration. We confirm availability before describing it as part of a live workflow.</p>
          </div>
          <div>
            <h3 className="mb-1 font-semibold">Can it hand a conversation to our team?</h3>
            <p className="text-sm text-gray-600">The handoff process can be scoped around your support workflow. Actual email or helpdesk delivery must be configured and tested for your setup.</p>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="text-center">
        <h3 className="mb-2 text-xl font-semibold">Validate the assistant before installing it.</h3>
        <p className="mb-5 text-sm text-gray-700">Start with a private Shopify preview, test real questions, and choose the level of support that fits.</p>
        <div className="flex flex-col justify-center gap-3 sm:flex-row">
          <Link to="/trial?plan=growth&channel=shopify" className="rounded-lg bg-indigo-600 px-6 py-3 text-white hover:bg-indigo-700">Start 7-day Shopify trial</Link>
          <Link to="/demo/rag?mode=dtc_supplements" className="rounded-lg border px-6 py-3 hover:bg-gray-50">Preview demo first</Link>
          <Link to="/trial?plan=starter&channel=shopify" className="px-6 py-3 text-indigo-600 hover:underline">Start Starter trial</Link>
        </div>
      </section>
    </div>
  );
}
